'use client'
import lachnerLogo from '../../../public/lachner-logo.svg'
import reanallaborLogo from '../../../public/reanallabor-logo.jpg'
import Image from 'next/image'
import Link from 'next/link'
import { NavbarWrapper } from '@/app/(cms)/Navbar'
import useResponsive from '@/hooks/useResponsive'
import LanguageSwitcher from '@/components/LanguageSwitcher'
import CurrencySwitcher from '../CurrencySwitcher'
import { NavbarUser } from './User'
import { useCallback, useContext } from 'react'
import { EshopContext } from '../EshopProvider'
import NavbarBasketButton from './BasketButton'
import { useTranslation } from 'react-i18next'
import NavbarSearch from './Search'
import { useUser } from '@/eshop/hooks/useUser'
import { Language } from '@/utils/language'
import { usePathname } from 'next/navigation'
import { cn } from '@/utils/styles'
import { Currency } from '@/utils/currency'

interface Props {
    nativeLanguage: Language
    nativeCurrency: Currency
}

const logoHomeHref =
    process.env.NODE_ENV === 'production' ? 'https://lach-ner.com/' : '/'

export default function EshopNavbar({ nativeLanguage, nativeCurrency }: Props) {
    const { isDesktop } = useResponsive()
    const { setShowMobileMenu } = useContext(EshopContext)
    const { t } = useTranslation()
    const user = useUser()

    const pathname = usePathname()

    const handleMobileMenuClick = useCallback(() => {
        setShowMobileMenu(true)
    }, [setShowMobileMenu])

    return (
        <NavbarWrapper className="navbar">
            <div className="navbar-start flex items-center h-full">
                <Link href={logoHomeHref} className="my-auto">
                    <div
                        style={{
                            position: 'relative',
                            width: '100px',
                            height: '50px',
                        }}
                    >
                        <Image
                            src={
                                nativeLanguage === 'hu'
                                    ? reanallaborLogo
                                    : lachnerLogo
                            }
                            alt="Lachner logo"
                            fill
                            sizes="100px"
                            style={{ objectFit: 'contain' }}
                        />
                    </div>
                </Link>
                {isDesktop && (
                    <div className="flex items-end justify-center h-full mt-1 ml-4">
                        <Link
                            href="/eshop"
                            className={cn(
                                'mx-3 h-full text-base text-blue-dark flex hover:underline cursor-pointer underline-offset-[6px]',
                                pathname === '/eshop'
                                    ? 'underline'
                                    : 'opacity-60'
                            )}
                        >
                            {t('eshop.navbar.products')}
                        </Link>
                        {!!user && (
                            <Link
                                href="/eshop/user/orders"
                                className={cn(
                                    'mx-3 text-base hidden xl:block hover:underline cursor-pointer underline-offset-[6px]',
                                    pathname === '/eshop/user/orders'
                                        ? 'underline'
                                        : 'opacity-60'
                                )}
                            >
                                {t('eshop.navbar.orders')}
                            </Link>
                        )}
                        <Link
                            href="/eshop/documents"
                            className={cn(
                                'mx-3 text-base hidden xl:block hover:underline cursor-pointer underline-offset-[6px]',
                                pathname === '/eshop/documents'
                                    ? 'underline'
                                    : 'opacity-60'
                            )}
                        >
                            {t('eshop.navbar.documents')}
                        </Link>
                    </div>
                )}
            </div>

            <div className="navbar-end flex justify-end text-md">
                <div className="">
                    {isDesktop ? (
                        <div className="flex">
                            <NavbarSearch />
                            <LanguageSwitcher nativeLanguage={nativeLanguage} />
                            <CurrencySwitcher nativeCurrency={nativeCurrency} />
                            <NavbarUser />
                            <NavbarBasketButton className="ml-4 mr-4" />
                        </div>
                    ) : (
                        <>
                            <NavbarBasketButton className="mr-4" />
                            <button
                                className="btn btn-square btn-ghost"
                                onClick={handleMobileMenuClick}
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    className="inline-block w-5 h-5 stroke-current"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth="2"
                                        d="M4 6h16M4 12h16M4 18h16"
                                    ></path>
                                </svg>
                            </button>
                        </>
                    )}
                </div>
            </div>
        </NavbarWrapper>
    )
}
